import { graphql } from "@/shared/gql";
import { useQuery, useSuspenseQuery } from "@apollo/client";
import { useCompanyId } from "@/entities/company/hooks/use-company-id";
import { EntityNotFoundError } from "@/shared/lib/errors/404-not-found-error";
import { getPreloadQuery } from "@/shared/gql/apollo-client";

export const FetchCompanyTender = graphql(`
  query FetchCompanyTenders($companyId: ID!, $tenderId: ID!) {
    currentUser(company: $companyId) {
      id
      tender(id: $tenderId) {
        id
        ...AllTenderProps
        # Just the flags, for the count the title states. The chain itself is
        # loaded by the Timeline tab, which needs every node's tender, lots and
        # awards to draw it; the header only has to say how many rounds there
        # are, and it has to say it in the same round trip as the title or the
        # pill would pop in after the fact.
        reprocurementChain {
          nodes {
            isSource
          }
        }
      }
    }
  }
`);

/**
 * The header's slice of the tender: the fields the tender list already cached
 * for every row it drew. Warm — which is every click from a list, a kanban card
 * or a search result — this resolves from the cache without suspending, so the
 * page opens on its own title, publishers and tab strip and only the sections
 * that need more (the summary, the details panel) carry a skeleton. Cold, it is
 * one small query that lands well ahead of the full one.
 */
export const FetchTenderShell = graphql(`
  query FetchTenderShell($companyId: ID!, $tenderId: ID!) {
    currentUser(company: $companyId) {
      id
      tender(id: $tenderId) {
        id
        ...TenderShellProps
      }
    }
  }
`);

export const useFetchTenderShellQuery = (tenderId: string) => {
  const companyId = useCompanyId();
  const { data, error } = useSuspenseQuery(FetchTenderShell, {
    variables: { companyId, tenderId },
  });

  if (error) {
    throw error;
  }

  if (!data.currentUser.tender) {
    throw new EntityNotFoundError("Tender");
  }

  return data.currentUser.tender;
};

export const useFetchCompanyTenderQuery = (tenderId: string) => {
  const companyId = useCompanyId();
  const { data, error } = useSuspenseQuery(FetchCompanyTender, {
    variables: { companyId, tenderId },
  });

  if (error) {
    throw error;
  }

  if (!data.currentUser.tender) {
    throw new EntityNotFoundError("Tender");
  }

  return {
    tender: data.currentUser.tender,
  };
};

/**
 * The full tender if it has already arrived, and nothing if it has not.
 *
 * For the few header details the list does not cache — whether the tender is
 * shared, when it last synced, how long its procurement chain is, whether it
 * has been rectified. They ride along with the body's own query rather than
 * holding the header back: a share dot or an extra tab appearing a moment later
 * costs nothing, and blocking the title on them costs the whole page.
 */
export const useLoadedCompanyTender = (tenderId: string) => {
  const companyId = useCompanyId();
  const { data } = useQuery(FetchCompanyTender, {
    variables: { companyId, tenderId },
    fetchPolicy: "cache-first",
  });

  return data?.currentUser.tender ?? undefined;
};

// Warms the Apollo cache during route transition; the component reads
// the data via useSuspenseQuery which hits the pre-warmed cache.
export const preloadTenderDetailQuery = (
  companyId: string,
  tenderId: string,
) => {
  getPreloadQuery()(FetchCompanyTender, {
    variables: { companyId, tenderId },
  });
};

// Only does anything on a cold landing — from a list the shell is already in
// the cache. Fired beside the detail query so the two travel in one batch and
// the header does not wait for its own round trip.
export const preloadTenderShellQuery = (
  companyId: string,
  tenderId: string,
) => {
  getPreloadQuery()(FetchTenderShell, {
    variables: { companyId, tenderId },
  });
};
